import { Component } from 'opus-angular-seed-core';
import $ from 'jquery';
import { stripTrailingEmptyParagraphs } from '../../services/htmlContent';

@Component({
    bindings: {
        ngModel: '=',
        chapter: '=',
        apiUrl: '='
    },
    template: `
        <div ng-if="vm.editorLoading">... loading</div>
        <div ng-class="{'hidden': vm.editorLoading}">
            <input id="{{ vm.inputId }}" type="file" name="my-file" style="display: none;" onchange="" />
            <div class="{{ vm.inputId }}">
                <textarea class="tox-authoring-tool" />
            </div>
        </div>
  `
})
export default class {
    constructor ($scope, $rootScope, $translate, $http, $q, lodash, TokenService, UploadProgressService,
        ChunkUploadService
    ) {
        this.$scope = $scope;
        this.$translate = $translate;
        this.$rootScope = $rootScope;
        this.$http = $http;
        this.$q = $q;
        this.lodash = lodash;
        this.TokenService = TokenService;
        this.UploadProgressService = UploadProgressService;
        this.ChunkUploadService = ChunkUploadService;
        this.contentUpdateIntervalId = null;
        this.loading = false;
        this.editorLoading = false;
    }

    $onInit () {
        this.inputId = this.generateId();
        this.initEditor();

        this.$rootScope.$on('tinymce:removeContent', (event, data) => {
            if (data.hash === this.chapter.hash) {
                window.tinymce.activeEditor.setContent('');
                this.ngModel = null;
            }
        });
        this.$rootScope.$on('tinymce:loading', (event, loading, blockClassname) => {
            const selector = `.${blockClassname} .tox-preloader`;
            const preloader = document.querySelector(selector);
            this.loading = loading;

            if (loading) {
                preloader.classList.remove('hidden');
            }
            else {
                preloader.classList.add('hidden');
            }
        });
        this.$rootScope.$on('tinymce:editorLoading', (event, editorLoading) => {
            this.editorLoading = editorLoading;

            if (editorLoading) {
                document.body.classList.remove('preview-mode');
            }
            else {
                document.body.classList.add('preview-mode');
            }
        });
    }

    generateId (prefix = null) {
        const id = Math.random().toString(36).replace(/[^a-z]+/g, '').slice(0, 5);

        return prefix
            ? `${prefix}-${id}`
            : id;
    }
    // TinyMCE language packs are loaded in index.ejs; any other language uses the built-in English
    getEditorLanguage () {
        const editorLanguages = { ET: 'et', LT: 'lt' };

        return editorLanguages[this.TokenService.getActiveLanguage()] || 'en';
    }

    initEditor () {
        window.tinymce.init({
            language: this.getEditorLanguage(),
            branding: false,
            selector: 'textarea',
            toolbar_mode: 'wrap', // Prevents collapsing into three dots
            content_css: 'assets/tinymce/styles/editor.css',
            content_style: 'table { width: 100%; table-layout: auto; }',
            plugins: 'image media link fullscreen lists table',
            // media_default_width: 900,
            // media_default_height: Math.round(900 * 9 / 16), // 393
            toolbar: [
                'image media link|table|bullist numlist|undo redo',
                'blocks fontfamily fontsize|bold italic underline|forecolor backcolor' +
                    '|alignleft aligncenter alignright alignjustify|lineheight outdent indent'
            ],
            font_size_formats: '8pt 10pt 12pt 14pt 16pt 18pt 20pt 22pt 24pt 28pt 32pt 36pt 48pt 72pt',
            block_formats: 'Paragraph=p; Heading 1=h1; Heading 2=h2; Heading 3=h3; Heading 4=h4; Heading 5=h5; Heading 6=h6',
            extended_valid_elements: 'iframe[style|class|src|frameborder|alt|title|width|height|align|name|allowfullscreen|allow], video[controls|autoplay|loop|muted|poster|preload|width|height|src]',
            link_default_target: '_blank',  // Set default target to open in a new window
            link_context_toolbar: false,    // Hide context toolbar (optional)
            noneditable_regexp: /<div class="media-selected-wrapper.*?<\/div>/g,
            noneditable_class: 'noneditable',
            images_reuse_filename: true,
            image_dimensions: false, // very important
            schema: 'html5',
            menu: {
                file: { title: 'File', items: 'print' }
            },
            chapter_hash: this.chapter.hash,
            token: this.TokenService.token,
            content: this.ngModel,
            input_id: this.inputId,
            apiUrl: this.apiUrl,
            file_picker_types: 'file image media',
            file_picker_callback: (callback, value, meta) => {
                this.uploadFile(callback, meta, this);
            },
            paste_preprocess: (_editor, args) => {
                // Clean Word/Office HTML while preserving table structure
                if (!args.content) return;
                let html = args.content;

                // Detect Word content
                const isWordContent = html.includes('mso-') || html.includes('urn:schemas-microsoft') || html.includes('class="Mso');
                if (!isWordContent) return;

                // Remove Word-only conditional blocks <!--[if ...]>...<![endif]--> (content hidden from browsers)
                html = html.replace(/<!--\[if[^\]]*\](?!-)>[\s\S]*?<!\[endif\]-->/gi, '');
                // Strip browser-visible conditional markers, keeping content between them
                html = html.replace(/<!--\[if[^\]]*\]-->/gi, '');
                html = html.replace(/<!--\[endif\]-->/gi, '');
                // Remove regular HTML comments
                html = html.replace(/<!--[\s\S]*?-->/g, '');
                // Remove Word XML tags (<o:p>, <w:sdt>, etc.)
                html = html.replace(/<\/?[a-z]+:[^>]*>/gi, '');
                // Remove mso-* styles but keep other styles
                html = html.replace(/\s*mso-[^;:"']+:[^;:"']+;?/gi, '');
                // Remove Word-specific styles (margin/text-indent/tab-stops in absolute units)
                html = html.replace(/\s*margin-left:\s*-?[\d.]+(?:pt|cm|mm|in);?/gi, '');
                html = html.replace(/\s*margin-right:\s*-?[\d.]+(?:pt|cm|mm|in);?/gi, '');
                html = html.replace(/\s*text-indent:\s*-?[\d.]+(?:pt|cm|mm|in);?/gi, '');
                html = html.replace(/\s*tab-stops:[^;:"']+;?/gi, '');
                // Clean Word table layout using DOM parsing
                const pasteDoc = new DOMParser().parseFromString(html, 'text/html');
                pasteDoc.querySelectorAll('table, col, colgroup, td, th').forEach(el => {
                    el.removeAttribute('width');
                    el.removeAttribute('cellspacing');
                    el.removeAttribute('cellpadding');
                    el.removeAttribute('border');
                    el.style.removeProperty('width');
                });
                html = pasteDoc.body.innerHTML;
                // Remove empty style attributes
                html = html.replace(/\s*style="\s*"/gi, '');
                // Remove Word-specific class attributes
                html = html.replace(/\s*class="Mso[^"]*"/gi, '');
                // Remove empty spans
                html = html.replace(/<span[^>]*>\s*<\/span>/gi, '');

                args.content = html;
            },
            init_instance_callback: (editor) => {
                editor.on('BeforeSetContent', (e) => {
                    // Skip processing for undo/redo operations or full content restoration
                    // This prevents the entire content from being replaced with just a video on undo
                    const parser = new DOMParser();
                    const doc = parser.parseFromString(e.content, 'text/html');

                    // If content has multiple top-level elements, it's likely an undo/redo operation
                    // restoring full document content - skip transformation
                    const mediaElements = ['VIDEO', 'IFRAME', 'IMG', 'FIGURE', 'EMBED'];
                    const topLevelElements = Array.from(doc.body.children).filter(el => {
                        if (el.tagName === 'BR') return false;
                        // Count media elements even if they have no text content
                        if (mediaElements.includes(el.tagName)) return true;
                        if (el.querySelector('video, iframe, img, figure, embed')) return true;
                        return el.textContent.trim() !== '';
                    });
                    if (topLevelElements.length > 1) {
                        return;
                    }

                    // Skip re-wrapping if content already has a media-selected-wrapper (e.g. drag-and-drop move)
                    if (doc.querySelector('figure.media-selected-wrapper')) {
                        return;
                    }

                    const iframe = doc.querySelector('iframe');
                    const embed = doc.querySelector('embed');
                    const blockquote = doc.querySelector('blockquote.instagram-media');
                    const element = iframe || embed || blockquote;

                    if (element) {
                        const originalEmbedHtml = element.outerHTML;
                        const originalWidth = parseInt(element.getAttribute('width'), 10);
                        const originalHeight = parseInt(element.getAttribute('height'), 10);
                        const wrapperWidth = 900;
                        let wrapperHeight = null;

                        if (originalWidth && originalHeight) {
                            wrapperHeight = Math.round(originalHeight * wrapperWidth / originalWidth);
                        }

                        e.content = this.wrapWithMediaSelectedWrapper(element, wrapperWidth, wrapperHeight, originalEmbedHtml);
                        return;
                    }

                    const img = doc.querySelector('img');
                    if (img) {
                        const realImage = new Image();
                        realImage.onload = () => {
                            const naturalWidth = realImage.naturalWidth;
                            const naturalHeight = realImage.naturalHeight;
                            const maxWidth = 900;
                            if (naturalWidth > maxWidth) {
                                const ratio = naturalHeight / naturalWidth;
                                img.setAttribute('width', maxWidth);
                                img.setAttribute('height', Math.round(maxWidth * ratio));
                            }
                            else {
                                img.setAttribute('width', naturalWidth);
                                img.setAttribute('height', naturalHeight);
                            }
                            e.content = doc.body.innerHTML;
                        };
                        realImage.src = img.src;
                        return;
                    }

                    const href = this.getLinkFromMedia(e.content);
                    if (href !== null) {
                        e.content = this.parseContentFromMedia(e, href); // <- your existing logic
                    }
                });

                const iframeDocument = editor.iframeElement.contentDocument;

                iframeDocument.addEventListener('mousedown', (e) => {
                    const handle = e.target;
                    const figure = handle.closest('figure.media-selected-wrapper');
                    if (!figure) return;

                    const isResizeHandle = handle.classList.contains('resize-handle') || handle.classList.contains('resize-handle-vertical');
                    const isToolbarButton = handle.classList.contains('media-toolbar-button');

                    if (!isResizeHandle && !isToolbarButton) return;

                    e.preventDefault();
                    e.stopPropagation();

                    if (handle.classList.contains('resize-handle')) {
                        const iframe = figure.querySelector('iframe');
                        const preview = figure.querySelector('.mce-object-iframe, .mce-object-embed');

                        const startX = e.clientX;
                        const startWidth = figure.offsetWidth;

                        figure.style.aspectRatio = 'auto';

                        const onMouseMove = (moveEvent) => {
                            const dx = moveEvent.clientX - startX;
                            let newWidth = startWidth + dx;
                            newWidth = Math.max(100, Math.min(870, newWidth));

                            figure.style.width = `${newWidth}px`;

                            if (iframe) {
                                iframe.style.width = '100%';
                            }
                            if (preview) {
                                preview.style.width = '100%';
                            }
                        };

                        const onMouseUp = () => {
                            iframeDocument.removeEventListener('mousemove', onMouseMove);
                            iframeDocument.removeEventListener('mouseup', onMouseUp);
                            editor.nodeChanged();
                            this.ngModel = stripTrailingEmptyParagraphs(editor.getContent());
                        };

                        iframeDocument.addEventListener('mousemove', onMouseMove);
                        iframeDocument.addEventListener('mouseup', onMouseUp);
                    }

                    if (handle.classList.contains('resize-handle-vertical')) {
                        const startY = e.clientY;
                        const startHeight = figure.offsetHeight;

                        figure.style.aspectRatio = 'auto';

                        const onMouseMove = (moveEvent) => {
                            const dy = moveEvent.clientY - startY;
                            let newHeight = startHeight + dy;
                            newHeight = Math.max(100, newHeight);

                            figure.style.height = `${newHeight}px`;

                            const preview = figure.querySelector('.mce-object-iframe, .mce-object-embed');
                            if (preview) {
                                preview.style.height = `${newHeight}px`;
                                preview.setAttribute('height', newHeight);
                            }
                            const iframe = figure.querySelector('iframe');
                            if (iframe) {
                                iframe.style.height = `${newHeight}px`;
                            }

                            // AUTO SCROLL DOWN IF MOUSE IS NEAR BOTTOM
                            const iframeDoc = editor.iframeElement.contentDocument;
                            const html = iframeDoc.documentElement;

                            const currentHeight = parseInt(figure.style.height, 10);
                            const bodyScrollHeight = html.scrollHeight;

                            if (currentHeight + 200 > bodyScrollHeight) {
                                html.style.height = `${currentHeight + 200}px`;

                                setTimeout(() => {
                                    html.scrollTop = html.scrollHeight;
                                }, 0);
                            }
                        };

                        const onMouseUp = () => {
                            iframeDocument.removeEventListener('mousemove', onMouseMove);
                            iframeDocument.removeEventListener('mouseup', onMouseUp);
                            editor.nodeChanged();
                            this.ngModel = stripTrailingEmptyParagraphs(editor.getContent());
                        };

                        iframeDocument.addEventListener('mousemove', onMouseMove);
                        iframeDocument.addEventListener('mouseup', onMouseUp);
                    }
                });
            },
            setup: (editor) => {
                // Track the last selected material preview for the media dialog
                let lastSelectedMaterialSource = null;
                let lastSelectedEmbedHtml = null;

                // Update tracked source on selection/node change
                editor.on('NodeChange', () => {
                    const selectedNode = editor.selection.getNode();

                    // Highlight "Insert/edit media" button when inside a media-selected-wrapper
                    const editorContainer = editor.getContainer();
                    const mediaButton = editorContainer && (
                        editorContainer.querySelector('.tox-toolbar button[aria-label="Insert/edit media"]') ||
                        editorContainer.querySelector('.tox-toolbar button[title="Insert/edit media"]')
                    );
                    if (mediaButton) {
                        const inWrapper = selectedNode && selectedNode.closest && selectedNode.closest('figure.media-selected-wrapper');
                        if (inWrapper) {
                            mediaButton.classList.add('tox-tbtn--enabled');
                        }
                        else {
                            // Only remove if not on a native media element (TinyMCE handles those itself)
                            const onNativeMedia = selectedNode && selectedNode.closest && (
                                selectedNode.closest('video') || selectedNode.closest('.mce-preview-object')
                            );
                            if (!onNativeMedia) {
                                mediaButton.classList.remove('tox-tbtn--enabled');
                            }
                        }
                    }

                    if (!selectedNode) {
                        lastSelectedMaterialSource = null;
                        return;
                    }

                    // Check if selection is on a material-preview wrapper
                    const wrapper = selectedNode.closest && selectedNode.closest('figure.media-selected-wrapper');
                    if (wrapper) {
                        const iframe = wrapper.querySelector('iframe.material-preview') ||
                            wrapper.querySelector('iframe[src*="material/content"]');
                        if (iframe) {
                            const src = iframe.getAttribute('src') || '';
                            const pathMatch = src.match(/[?&]path=([^&]+)/);
                            if (pathMatch) {
                                lastSelectedMaterialSource = decodeURIComponent(pathMatch[1]);
                                lastSelectedEmbedHtml = null;
                                return;
                            }
                        }
                        // Generic iframe/embed inside wrapper (e.g. H5P, YouTube embed code)
                        const storedEmbed = wrapper.getAttribute('data-embed-html');
                        if (storedEmbed) {
                            lastSelectedEmbedHtml = decodeURIComponent(storedEmbed);
                            lastSelectedMaterialSource = null;
                            return;
                        }
                    }

                    // Check for mce-preview-object
                    const previewObj = selectedNode.closest && selectedNode.closest('.mce-preview-object');
                    if (previewObj) {
                        const originalHtml = previewObj.getAttribute('data-mce-html');
                        if (originalHtml) {
                            const decodedHtml = decodeURIComponent(originalHtml);
                            if (decodedHtml.includes('material-preview') || decodedHtml.includes('material/content')) {
                                const pathMatch = decodedHtml.match(/[?&]path=([^&"']+)/);
                                if (pathMatch) {
                                    lastSelectedMaterialSource = decodeURIComponent(pathMatch[1]);
                                    lastSelectedEmbedHtml = null;
                                    return;
                                }
                            }
                            // Generic embed via mce-preview-object
                            lastSelectedEmbedHtml = decodedHtml;
                            lastSelectedMaterialSource = null;
                            return;
                        }
                    }

                    // Not on a media element - reset
                    lastSelectedMaterialSource = null;
                    lastSelectedEmbedHtml = null;
                });

                editor.on('click', (e) => {
                    // Check if clicked on a material-preview wrapper or its contents
                    const target = e.target;
                    const wrapper = target.closest('figure.media-selected-wrapper');
                    if (wrapper) {
                        const iframe = wrapper.querySelector('iframe.material-preview') ||
                            wrapper.querySelector('iframe[src*="material/content"]');
                        if (iframe) {
                            const src = iframe.getAttribute('src') || '';
                            const pathMatch = src.match(/[?&]path=([^&]+)/);
                            if (pathMatch) {
                                lastSelectedMaterialSource = decodeURIComponent(pathMatch[1]);
                                lastSelectedEmbedHtml = null;
                            }
                        }
                        else {
                            // Generic iframe/embed inside wrapper (e.g. H5P, YouTube embed code)
                            const storedEmbed = wrapper.getAttribute('data-embed-html');
                            if (storedEmbed) {
                                lastSelectedEmbedHtml = decodeURIComponent(storedEmbed);
                                lastSelectedMaterialSource = null;
                            }
                        }
                    }
                    else {
                        // Check for mce-preview-object (TinyMCE's iframe placeholder)
                        const previewObj = target.closest('.mce-preview-object');
                        if (previewObj) {
                            const originalHtml = previewObj.getAttribute('data-mce-html');
                            if (originalHtml) {
                                const decodedHtml = decodeURIComponent(originalHtml);
                                if (decodedHtml.includes('material-preview') || decodedHtml.includes('material/content')) {
                                    const pathMatch = decodedHtml.match(/[?&]path=([^&"']+)/);
                                    if (pathMatch) {
                                        lastSelectedMaterialSource = decodeURIComponent(pathMatch[1]);
                                        lastSelectedEmbedHtml = null;
                                    }
                                }
                                else {
                                    lastSelectedMaterialSource = null;
                                    lastSelectedEmbedHtml = decodedHtml;
                                }
                            }
                        }
                        else {
                            lastSelectedMaterialSource = null;
                            lastSelectedEmbedHtml = null;
                        }
                    }
                });

                // hide "Open link in..." input in "Insert/Edit Link" modal
                // and populate source field for custom material-preview iframes in media dialog
                editor.on('OpenWindow', () => {
                    const linkDialog = document.querySelector('.tox-dialog');
                    // iterate all "tox-form__group"
                    const formGroups = linkDialog.querySelectorAll('.tox-form__group');
                    this.lodash.forEach(formGroups, formGroup => {
                        const label = formGroup.querySelector('.tox-label');
                        if (
                            label &&
                            (label.textContent.includes('Open link in') || label.textContent.includes('Ava link saidil'))
                          ) {
                            formGroup.style.display = 'none'; // Hides the element
                        }
                    });

                    // Check if this is a media dialog and populate source for custom material previews
                    const dialogTitle = linkDialog && linkDialog.querySelector('.tox-dialog__title');
                    const isMediaDialog = dialogTitle && (
                        dialogTitle.textContent.includes('Media') ||
                        dialogTitle.textContent.includes('media')
                    );

                    if (isMediaDialog && lastSelectedMaterialSource) {
                        // Populate the source field with the tracked material source
                        setTimeout(() => {
                            const sourceInput = linkDialog.querySelector('input.tox-textfield');
                            if (sourceInput && !sourceInput.value) {
                                sourceInput.value = lastSelectedMaterialSource;
                                sourceInput.dispatchEvent(new Event('input', { bubbles: true }));
                            }
                        }, 50);
                    }

                    if (isMediaDialog && lastSelectedEmbedHtml) {
                        // Switch to Embed tab and populate the textarea with the embed code
                        setTimeout(() => {
                            // Find and click the Embed tab
                            const tabs = linkDialog.querySelectorAll('.tox-dialog__body-nav-item');
                            let embedTab = null;
                            tabs.forEach(tab => {
                                if (tab.textContent.trim() === 'Embed') {
                                    embedTab = tab;
                                }
                            });
                            if (embedTab) {
                                embedTab.click();
                                // After switching tab, populate the textarea
                                setTimeout(() => {
                                    const embedTextarea = linkDialog.querySelector('textarea.tox-textarea');
                                    if (embedTextarea) {
                                        embedTextarea.value = lastSelectedEmbedHtml;
                                        embedTextarea.dispatchEvent(new Event('input', { bubbles: true }));
                                    }
                                }, 50);
                            }
                        }, 50);
                    }
                });
                editor.on('ObjectResized', (e) => {
                    if (e.target.nodeName === 'IMG') {
                        const maxWidth = 900;
                        if (e.width > maxWidth) {
                            e.target.style.width = `${maxWidth}px`;
                            e.target.removeAttribute('height'); // remove manual height to keep aspect ratio
                            e.preventDefault();
                        }
                    }
                });
                // Make toolbar Undo/Redo buttons work without scroll jump
                editor.on('init', () => {
                    const toolbar = document.querySelector('.tox-toolbar');
                    if (toolbar) {
                        toolbar.addEventListener('click', (e) => {
                            const button = e.target.closest('button');
                            if (!button) return;

                            const isUndo = button.title === 'Undo' || button.getAttribute('aria-label') === 'Undo';
                            const isRedo = button.title === 'Redo' || button.getAttribute('aria-label') === 'Redo';

                            if (isUndo || isRedo) {
                                e.preventDefault();
                                e.stopPropagation();

                                // Save scroll position
                                const iframeDoc = editor.iframeElement.contentDocument;
                                const scrollTop = iframeDoc.documentElement.scrollTop || iframeDoc.body.scrollTop;

                                // Use undoManager directly
                                if (isUndo) {
                                    editor.undoManager.undo();
                                }
                                else {
                                    editor.undoManager.redo();
                                }

                                // Restore scroll position
                                setTimeout(() => {
                                    iframeDoc.documentElement.scrollTop = scrollTop;
                                    iframeDoc.body.scrollTop = scrollTop;
                                }, 0);
                            }
                        }, true);
                    }
                });

                editor.on('SetContent', () => {
                    const iframeDocument = editor.iframeElement.contentDocument;

                    if (!iframeDocument.getElementById('instagram-embed-script')) {
                        const script = iframeDocument.createElement('script');
                        script.id = 'instagram-embed-script';
                        script.src = 'https://www.instagram.com/embed.js';
                        iframeDocument.body.appendChild(script);
                    }
                    else if (iframeDocument.defaultView.instgrm) {
                        iframeDocument.defaultView.instgrm.Embeds.process();
                    }
                });

                editor.on('click', (e) => {
                    const target = e.target;
                    if (!target.classList.contains('media-toolbar-button')) return;

                    e.preventDefault();
                    e.stopPropagation();

                    const figure = target.closest('figure.media-selected-wrapper');
                    if (!figure) return;

                    if (target.classList.contains('move-up')) {
                        const prev = figure.previousElementSibling;
                        if (prev) {
                            prev.before(figure);
                        }
                    }
                    else if (target.classList.contains('move-down')) {
                        const next = figure.nextElementSibling;
                        if (next) {
                            next.after(figure);
                            // After moving, check if still something after
                            if (!figure.nextElementSibling) {
                                const newParagraph = document.createElement('p');
                                figure.parentNode.appendChild(newParagraph);
                            }
                        }
                    }
                    else if (target.classList.contains('resize')) {
                        const currentWidth = figure.style.width.replace('px', '') || '900';
                        const newWidth = prompt('Enter new width in pixels (max 900):', currentWidth);
                        if (newWidth !== null) {
                            const parsed = Math.min(Math.max(parseInt(newWidth, 10), 100), 900); // between 100 and 900
                            figure.style.width = `${parsed} + 'px'`;

                            let style = figure.getAttribute('style') || '';
                            style = style.replace(/width: ?\d+px/, `width: ${parsed}px`);
                            figure.setAttribute('style', style);
                        }
                    }
                    else if (target.classList.contains('resize-handle') || target.classList.contains('resize-handle-vertical')) {
                        // Clicking resize dots - do nothing
                        return;
                    }
                    else {
                        // Default: Delete button
                        figure.remove();
                    }
                });

                editor.on('drop', (event) => {
                    const files = event.dataTransfer.files;
                    if (files.length > 0) {
                        for (let i = 0; i < files.length; i++) {
                            this.uploadDroppedFile(files[i], editor, this.$rootScope, this.inputId);
                        }
                        event.preventDefault();
                    }
                });
                editor.on('init', () => {
                    const content = editor.getParam('content');
                    if (content !== undefined) {
                        // Drop any blank lines stored from earlier cycles; TinyMCE itself keeps a
                        // trailing editable line, so we don't add one (that produced double lines).
                        editor.setContent(stripTrailingEmptyParagraphs(content) || '');
                    }

                    // Select the tox-editor-header element
                    const selector = `.${this.inputId} .tox-editor-header`;
                    const editorHeader = document.querySelector(selector);

                    // Create a new div element
                    const preloaderDiv = document.createElement('div');
                    preloaderDiv.className = 'tox-preloader hidden';
                    preloaderDiv.innerHTML = `
                    <div class="progress-container">
                        <div class="progress-bar"></div>
                    </div>`;

                    // Insert the new div after the tox-editor-header
                    editorHeader.parentNode.insertBefore(preloaderDiv, editorHeader.nextSibling);
                    this.$rootScope.$broadcast('tinymce:editorLoading', false);
                });
                editor.on('input NodeChange KeyUp change input cut delete submit', () => {
                    clearInterval(this.contentUpdateIntervalId);
                    this.contentUpdateIntervalId = setInterval(() => {
                        this.ngModel = stripTrailingEmptyParagraphs(editor.getContent());
                        clearInterval(this.contentUpdateIntervalId);
                    }, 50);
                });
            },
            media_live_embeds: true,
            paste_data_images: true,
            automatic_uploads: true,
            media_dimensions: false
        });
    }

    createButton (text, right, extraClass = '') {
        const button = document.createElement('button');
        button.textContent = text;
        button.className = `media-toolbar-button ${extraClass}`.trim();
        button.style.position = 'absolute';
        button.style.bottom = '-12px';
        button.style.right = right;
        button.style.background = '#007acc';
        button.style.color = '#fff';
        button.style.border = 'none';
        button.style.borderRadius = '4px';
        button.style.padding = '6px 10px';
        button.style.fontSize = '12px';
        button.style.cursor = 'pointer';
        button.style.zIndex = '10';
        button.style.lineHeight = '1';
        button.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
        button.style.transition = 'background 0.2s';
        button.style.display = 'none';
        button.setAttribute('contenteditable', 'true');
        button.setAttribute('tabindex', '-1');

        button.addEventListener('mouseover', () => {
            button.style.background = '#005f99';
        });
        button.addEventListener('mouseout', () => {
            button.style.background = '#007acc';
        });

        return button;
    }

    createToolbarButtons () {
        const buttons = [];

        const deleteButton = this.createButton('Delete', '20px');
        const moveUpButton = this.createButton('▲', '80px', 'move-up');
        const moveDownButton = this.createButton('▼', '112px', 'move-down');

        buttons.push(moveUpButton, moveDownButton, deleteButton);

        const resizeHandleVertical = document.createElement('button');
        resizeHandleVertical.className = 'media-toolbar-button resize-handle-vertical';
        resizeHandleVertical.style.position = 'absolute';
        resizeHandleVertical.style.bottom = '-7px';
        resizeHandleVertical.style.left = '50%';
        resizeHandleVertical.style.transform = 'translateX(-50%)';
        resizeHandleVertical.style.width = '12px';
        resizeHandleVertical.style.height = '12px';
        resizeHandleVertical.style.cursor = 'ns-resize';
        resizeHandleVertical.style.background = '#007acc';
        resizeHandleVertical.style.zIndex = '20';
        resizeHandleVertical.style.display = 'none';
        resizeHandleVertical.style.zIndex = '10';
        resizeHandleVertical.setAttribute('contenteditable', 'false');

        buttons.push(resizeHandleVertical);
        return buttons;
    }

    wrapWithMediaSelectedWrapper (element, targetWidth = 900, targetHeight = null, originalEmbedHtml = null) {
        const originalWidth = parseInt(element.getAttribute('width'), 10);
        const originalHeight = parseInt(element.getAttribute('height'), 10);

        // Remove original attributes and inline styles
        element.removeAttribute('width');
        element.removeAttribute('height');
        element.removeAttribute('style');

        // Fallback: calculate height by preserving aspect ratio
        if (!targetHeight && originalWidth && originalHeight) {
            targetHeight = Math.round(originalHeight * targetWidth / originalWidth);
        }

        // Apply styles
        element.style.width = `${targetWidth}px`;
        if (targetHeight) {
            element.style.height = `${targetHeight}px`;
        }
        element.style.border = '0';
        element.style.display = 'block';

        // Wrapper
        const wrapper = document.createElement('figure');
        wrapper.className = 'media-selected-wrapper';
        wrapper.setAttribute('contenteditable', 'false');
        wrapper.style.position = 'relative';
        wrapper.style.maxWidth = `${targetWidth}px`;
        wrapper.style.margin = '1em auto';
        wrapper.style.background = 'transparent';
        wrapper.style.padding = '0';
        wrapper.style.height = 'auto';

        if (originalEmbedHtml) {
            wrapper.setAttribute('data-embed-html', encodeURIComponent(originalEmbedHtml));
        }

        wrapper.appendChild(element);

        const buttons = this.createToolbarButtons();
        buttons.forEach(button => wrapper.appendChild(button));

        const container = document.createElement('div');
        container.appendChild(wrapper);

        const p = document.createElement('p');
        container.appendChild(p);

        return container.innerHTML;
    }

    uploadFile (callback, meta, self) {
        const showDialog = self.showDialog;
        const hideDialog = self.hideDialog;
        const $rootScope = self.$rootScope;
        const blockClassname = self.inputId;
        const editor = window.tinymce.activeEditor;
        const input = document.getElementById(editor.getParam('input_id'));

        // Reset the file input value each time to avoid retaining previous selection
        input.value = '';

        // Close dialog on abort (Cancel) in the file picker
        input.onabort = function () {
            hideDialog();
            showDialog();
            $rootScope.$broadcast('tinymce:loading', false, blockClassname);
        };

        // Event listener for "cancel" button in case the abort doesn't work as expected
        input.addEventListener('change', (event) => {
            if (event.target.files.length === 0) {
                hideDialog();
                showDialog();
                $rootScope.$broadcast('tinymce:loading', false, blockClassname);
            }
        });
        input.click();
        input.onchange = function () {
            hideDialog();
            const file = input.files[0];
            if (file === undefined) {
                return;
            }

            // the backend serves an image by the type of its extension, the same one the browser reports
            if (meta.filetype === 'image' && !file.type.startsWith('image/')) {
                self.UploadProgressService.reject(
                    file.name,
                    self.$translate.instant('general.Unsupported file format')
                );
                showDialog();
                return;
            }

            if (!self.validateFileSize(file)) {
                showDialog();
                return;
            }

            $rootScope.$broadcast('tinymce:loading', true, blockClassname);
            self.uploadChapterFile(file, meta.filetype, editor)
                .then((uploadedFile) => {
                    showDialog();
                    if (meta.filetype === 'file') {
                        callback(uploadedFile.url, {
                            text: file.name,
                            width: '100%',
                            height: '100%'
                        });
                    }
                    else {
                        callback(uploadedFile.url, {
                            alt: file.name,
                            width: '100%',
                            height: '100%'
                        });
                    }
                })
                .catch(() => showDialog())
                .finally(() => $rootScope.$broadcast('tinymce:loading', false, blockClassname));
        };

        setTimeout(() => {
            const event = new Event('change');
            input.dispatchEvent(event);
        }, 200);
    }

    uploadDroppedFile (file, editor, $rootScope, blockClassname) {
        const fileType = file.type;

        // the content depends only on the type, so a file that cannot be shown in the editor is not sent at all
        if (!this.getDroppedFileContent(file, fileType, '')) {
            this.UploadProgressService.reject(file.name, this.$translate.instant('general.Unsupported file format'));
            return;
        }

        if (!this.validateFileSize(file)) {
            return;
        }

        $rootScope.$broadcast('tinymce:loading', true, blockClassname);
        this.uploadChapterFile(file, 'media', editor)
            .then(uploadedFile => editor.insertContent(this.getDroppedFileContent(file, fileType, uploadedFile.url)))
            .catch(() => {})
            .finally(() => $rootScope.$broadcast('tinymce:loading', false, blockClassname));
    }

    getDroppedFileContent (file, fileType, href) {
        if (fileType.includes('video')) {
            return this.getVideoEmbedContent(href);
        }
        if (fileType.includes('image')) {
            return `<img src="${href}" alt="${file.name}" />`;
        }
        if (fileType.includes('pdf')) {
            return this.getMaterialEmbedContent(href, 'material-preview-pdf');
        }
        if (fileType.includes('msword') || fileType.includes('wordprocessingml.document')) {
            return this.getMaterialEmbedContent(href, 'material-preview-word');
        }
        if (fileType.includes('spreadsheetml.sheet') || fileType.includes('ods') || fileType.includes('opendocument.spreadsheet') || fileType.includes('vnd.ms-excel')) {
            return this.getMaterialEmbedContent(href, 'material-preview-excel');
        }
        if (fileType.includes('presentationml.presentation')) {
            return this.getMaterialEmbedContent(href, 'material-preview-powerpoint', 700);
        }
        if (fileType.includes('opendocument.text') || fileType.includes('odt')) {
            return this.getMaterialEmbedContent(href, 'material-preview-word');
        }
        if (fileType.includes('opendocument.presentation') || fileType.includes('vnd.ms-powerpoint')) {
            return this.getMaterialEmbedContent(href, 'material-preview-powerpoint', 700);
        }

        return '';
    }

    /**
     * Sends the file in chunks, then creates the chapter file from them. The
     * progress and the outcome are shown by the upload progress toaster, so the
     * callers only react to the result. Resolves with the created file ({ url }),
     * rejects with the error message.
     */
    uploadChapterFile (file, uploadType, editor) {
        const apiUrl = editor.getParam('apiUrl');
        const chunkId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
        const upload = this.UploadProgressService.start(file.name, file.size);

        return this.ChunkUploadService.upload(
            file,
            `${apiUrl}course/material-chapter-file/upload-chunk`,
            chunkId,
            loaded => this.UploadProgressService.setLoaded(upload, loaded)
        )
            .then(totalChunks => this.$http.post(`${apiUrl}course/material-chapter-file/create`, {
                chapterHash: editor.getParam('chapter_hash'),
                chunkId,
                totalChunks,
                filename: file.name,
                uploadType
            }))
            .then((response) => {
                this.UploadProgressService.succeed(upload);
                return response.data.file;
            })
            .catch((response) => {
                const message = this.ChunkUploadService.getErrorMessage(response);
                this.UploadProgressService.fail(upload, message);
                return this.$q.reject(message);
            });
    }

    validateFileSize (file) {
        let maxSizeMb = 150;

        // Video formats
        if (file.type.startsWith('video/')) {
            maxSizeMb = 1024;
        }

        // ZIP formats
        if (file.type === 'application/zip' ||
            file.type === 'application/x-zip-compressed') {
            maxSizeMb = 1000;
        }

        // Spreadsheet formats
        if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
            file.type === 'application/vnd.oasis.opendocument.spreadsheet' ||
            file.type === 'application/vnd.ms-excel') {
            maxSizeMb = 25;
        }

        // Word document formats
        if (file.type === 'application/msword' ||
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
            maxSizeMb = 25;
        }

        const maxSize = maxSizeMb * 1024 * 1024;
        if (file.size > maxSize) {
            this.UploadProgressService.reject(
                file.name,
                this.$translate.instant('general.File size exceeds limit', { mb: maxSizeMb })
            );
            return false;
        }
        return true;
    }

    convertToDomElement (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        return doc.body.firstElementChild;
    }

    getMaterialEmbedContent (href, className, height = 1200) {
        const baseUrl = window.location.origin;
        const iframeUrl = `${baseUrl}/#/material/content?path=${href}`;
        return `<iframe height="${height}" width="900" class="material-preview ${className}" src="${iframeUrl}"></iframe>`;
    }

    getVideoEmbedContent (href) {
        const id = this.generateId('plyr-player');
        return `<video preload="auto" class="authoring-tool-video" id="${id}" controls style="width: 100%; height: 100%; max-width:900px;">
                <source src="${href}" type="video/mp4">
                Your browser does not support the video tag.
            </video>`;
    }

    isWordContent (content) {
        return content.indexOf('application/msword') > 0 || content.indexOf('wordprocessingml.document') > 0;
    }

    isPdfContent (content) {
        return content.indexOf('application/pdf') > 0;
    }

    isPresentationContent (content) {
        return content.indexOf('presentationml.presentation') > 0 || content.indexOf('vnd.ms-powerpoint') > 0;
    }

    isVideoContent (content) {
        return content.indexOf('video/mp4') > 0 || content.indexOf('video/webm') > 0 || content.indexOf('video/ogg') > 0;
    }

    isExcelContent (content) {
        return content.indexOf('spreadsheetml.sheet') > 0 || content.indexOf('opendocument.spreadsheet') > 0 || content.indexOf('vnd.ms-excel') > 0;
    }

    isLinkContent (content) {
        return content.indexOf('target') <= 0;
    }

    parseContentFromMedia (e, href) {
        let useWrapper = false;
        let height = 1200;
        if (this.isPdfContent(e.content)) {
            e.content = this.getMaterialEmbedContent(href, 'material-preview-pdf');
            useWrapper = true;
        }
        else if (this.isWordContent(e.content)) {
            e.content = this.getMaterialEmbedContent(href, 'material-preview-word');
            useWrapper = true;
        }
        else if (this.isPresentationContent(e.content)) {
            e.content = this.getMaterialEmbedContent(href, 'material-preview-powerpoint', 700);
            useWrapper = true;
            height = 900;
        }
        else if (this.isExcelContent(e.content)) {
            e.content = this.getMaterialEmbedContent(href, 'material-preview-excel');
            useWrapper = true;
        }
        else if (this.isVideoContent(e.content)) {
            e.content = this.getVideoEmbedContent(href);
        }
        else {
            if (this.isLinkContent(e.content)) {
                e.content = `<a href="${href}" target="_blank" rel="noopener noreferrer">${href}</a>`;
            }
        }

        if (useWrapper) {
            e.content = this.wrapWithMediaSelectedWrapper(this.convertToDomElement(e.content), 900, height);
        }
        return e.content;
    }

    getHeightFromMedia (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const linkElement = doc.querySelector('video');
        if (linkElement !== null) {
            const hrefValue = linkElement.getAttribute('height');
            return hrefValue;
        }
        return null;
    }

    getWidthFromMedia (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const linkElement = doc.querySelector('video');
        if (linkElement !== null) {
            const hrefValue = linkElement.getAttribute('width');
            return hrefValue;
        }
        return null;
    }

    getLinkFromMedia (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const linkElement = doc.querySelector('source');
        if (linkElement) {
            let hrefValue = linkElement.getAttribute('src');

            // Check if it's an absolute URL
            if (!hrefValue.match(/^https?:\/\//) && !hrefValue.startsWith('//')) {
                const origin = window.location.origin;
                // Ensure we don't get double slashes
                if (hrefValue.startsWith('/')) {
                    hrefValue = `${origin}${hrefValue}`;
                }
                else {
                    hrefValue = `${origin}/${hrefValue}`;
                }
            }
            return hrefValue;
        }
        return null;
    }

    getLinkHref (content) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(content, 'text/html');
        const linkElement = doc.querySelector('a');
        if (linkElement !== null) {
            const hrefValue = linkElement.getAttribute('href');
            return hrefValue;
        }
        return null;
    }

    hideDialog () {
        $('.tox-dialog').hide();
    }

    showDialog () {
        $('.tox-dialog').show();
    }
}



// WEBPACK FOOTER //
// ./src/app/components/tinymce/index.js